import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2 } from "lucide-react";

const CUSTOMER_TYPES = ["school", "office", "healthcare", "government", "commercial", "other"];
const PRIORITIES = ["high", "medium", "low"];
const CLIENT_STATUSES = ["active", "inactive", "won", "lost"];
const LEAD_STATUSES = ["new", "contacted", "qualified", "unqualified", "converted"];

const EMPTY = {
  company_name: "", contact_name: "", phone: "", email: "",
  address: "", city: "", state: "", zip_code: "", county: "",
  sales_rep_id: "", customer_type: "other",
  priority: "medium", client_status: "active", lead_status: "new",
  next_followup_date: "", products_interested: "",
  estimated_value: "", notes: "",
};

export default function ClientDialog({ open, onOpenChange, client = null, reps = [], onSuccess }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (client) {
        setForm({ ...EMPTY, ...client, estimated_value: client.estimated_value ?? "" });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, client]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error("Company / organization name is required");
    setBusy(true);
    try {
      const payload = { ...form };
      payload.estimated_value = form.estimated_value === "" ? null : Number(form.estimated_value);
      if (!form.next_followup_date) payload.next_followup_date = null;
      if (!form.sales_rep_id) payload.sales_rep_id = null;
      if (client) {
        await api.patch(`/sales/clients/${client.id}`, payload);
        toast.success("Client updated");
      } else {
        await api.post("/sales/clients", payload);
        toast.success(`Added ${form.company_name}`);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {client ? "Edit client" : "New client"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Company or Organization *</Label>
            <Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="client-company-input" className="rounded-sm mt-1.5" placeholder="Macon County Health Department" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Contact person</Label>
              <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="client-contact-input" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Phone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="client-phone-input" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="client-email-input" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Customer type</Label>
              <Select value={form.customer_type} onValueChange={(v) => set("customer_type", v)}>
                <SelectTrigger data-testid="client-type-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CUSTOMER_TYPES.map((t) => (<SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Address</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="client-address-input" className="rounded-sm mt-1.5" placeholder="123 Main St" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">City</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="client-city-input" className="rounded-sm mt-1.5" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">State</Label>
                <Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="client-state-input" className="rounded-sm mt-1.5 uppercase" maxLength={2} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">ZIP</Label>
                <Input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid="client-zip-input" className="rounded-sm mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">County</Label>
                <Input value={form.county} onChange={(e) => set("county", e.target.value)} data-testid="client-county-input" className="rounded-sm mt-1.5" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="client-priority-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => (<SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Client status</Label>
              <Select value={form.client_status} onValueChange={(v) => set("client_status", v)}>
                <SelectTrigger data-testid="client-status-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CLIENT_STATUSES.map((p) => (<SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Lead status</Label>
              <Select value={form.lead_status} onValueChange={(v) => set("lead_status", v)}>
                <SelectTrigger data-testid="client-lead-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{LEAD_STATUSES.map((p) => (<SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Next follow-up date</Label>
              <Input type="date" value={form.next_followup_date || ""} onChange={(e) => set("next_followup_date", e.target.value)} data-testid="client-followup-input" className="rounded-sm mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Estimated value ($)</Label>
              <Input type="number" min={0} value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} data-testid="client-value-input" className="rounded-sm mt-1.5" placeholder="15000" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Products interested</Label>
            <Input value={form.products_interested} onChange={(e) => set("products_interested", e.target.value)} data-testid="client-products-input" className="rounded-sm mt-1.5" placeholder="Ergonomic chairs, filing cabinets" />
          </div>

          {reps.length > 0 && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Sales rep</Label>
              <Select value={form.sales_rep_id || "__unassigned__"} onValueChange={(v) => set("sales_rep_id", v === "__unassigned__" ? "" : v)}>
                <SelectTrigger data-testid="client-rep-select" className="rounded-sm mt-1.5"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                  {reps.map((r) => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Quick notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="client-notes-input" className="rounded-sm mt-1.5" placeholder="Short summary; the long-form memo lives on the client profile." />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="client-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              {client ? "Save changes" : "Create client"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
